import type {
  ConsignmentGathering,
  GatheringState,
  ParcelPlatform,
  ParcelTracking,
  ParcelTrackingStatus,
  PreAlertParcelRequest,
  WarehouseParcel,
  WarehouseParcelStatus,
  WarehouseView,
} from '../../lib/api/types'

/**
 * A customer's side of the China warehouse: labels, checking a pre-alert the
 * way the API will, and formatting prices, weights and flight weeks.
 */

export const PARCEL_PLATFORMS: readonly ParcelPlatform[] = [
  'TAOBAO',
  'JD',
  '1688',
  'PINDUODUO',
  'OTHER',
]

export const PLATFORM_LABELS: Record<ParcelPlatform, string> = {
  TAOBAO: 'Taobao',
  JD: 'JD',
  '1688': '1688',
  PINDUODUO: 'Pinduoduo',
  OTHER: 'Other',
}

/** Told the way a customer follows a parcel; the tones match the warehouse screen's. */
export const PARCEL_STATUS: Record<WarehouseParcelStatus, { label: string; tone: string }> = {
  EXPECTED: { label: 'On its way to the warehouse', tone: 'tone-waiting' },
  RECEIVED: { label: 'At the warehouse', tone: 'tone-moving' },
  CONSOLIDATED: { label: 'On a flight', tone: 'tone-done' },
  CANCELLED: { label: 'Cancelled', tone: 'tone-neutral' },
}

/** API errors whose own wording is meant for Xtellar staff, not customers. */
const CUSTOMER_MESSAGES: Record<string, string> = {
  NO_WAREHOUSE: 'Our warehouse is not taking parcels right now. Please try again later.',
  NO_RATE_CARD:
    'Your warehouse address and our prices are not available right now. Please try again later.',
}

export function parcelErrorMessage(error: unknown, fallback = 'Something went wrong. Try again.') {
  const code = (error as { code?: unknown } | null)?.code
  if (typeof code === 'string' && CUSTOMER_MESSAGES[code]) return CUSTOMER_MESSAGES[code]
  return error instanceof Error && error.message ? error.message : fallback
}

export interface PreAlertForm {
  trackingNumber: string
  platform: ParcelPlatform | ''
  orderNumber: string
  description: string
  quantity: string
  declaredValue: string
  dangerous: 'yes' | 'no' | ''
}

export const EMPTY_PRE_ALERT: PreAlertForm = {
  trackingNumber: '',
  platform: '',
  orderNumber: '',
  description: '',
  quantity: '',
  declaredValue: '',
  dangerous: '',
}

export type PreAlertErrors = Partial<Record<keyof PreAlertForm, string>>

/** Checks the form the way the API will, and turns it into the request body. */
export function buildPreAlert(
  form: PreAlertForm,
): { ok: true; parcel: PreAlertParcelRequest } | { ok: false; errors: PreAlertErrors } {
  const errors: PreAlertErrors = {}
  const trackingNumber = form.trackingNumber.trim()
  const orderNumber = form.orderNumber.trim()
  const description = form.description.trim()
  const quantityText = form.quantity.trim()
  const valueText = form.declaredValue.trim()

  if (trackingNumber.length > 60) errors.trackingNumber = 'Use at most 60 characters.'
  if (orderNumber.length > 80) errors.orderNumber = 'Use at most 80 characters.'
  if (!description) errors.description = 'Tell us what is inside.'
  else if (description.length > 500) errors.description = 'Use at most 500 characters.'

  const quantity = quantityText ? Number(quantityText) : null
  if (quantity !== null && (!Number.isInteger(quantity) || quantity < 1 || quantity > 100_000)) {
    errors.quantity = 'Enter a whole number, such as 3.'
  }

  const declaredValue = valueText ? Number(valueText) : null
  if (
    declaredValue !== null &&
    (!Number.isFinite(declaredValue) || declaredValue < 0 || declaredValue > 10_000_000)
  ) {
    errors.declaredValue = 'Enter the value in US dollars, such as 25.50.'
  }

  if (!form.dangerous) errors.dangerous = 'Please answer yes or no.'

  if (Object.keys(errors).length > 0) return { ok: false, errors }
  return {
    ok: true,
    parcel: {
      tracking_number: trackingNumber || null,
      platform: form.platform || null,
      order_number: orderNumber || null,
      description,
      quantity,
      declared_value: declaredValue,
      declared_dangerous: form.dangerous === 'yes',
    },
  }
}

export function formatMoney(amount: number, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export function formatKg(kg: number) {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(kg)} kg`
}

/** "2026-10-19" as "19 October 2026", read as a calendar date rather than UTC midnight. */
export function formatShipWeek(isoDate: string) {
  const [year, month, day] = isoDate.split('-').map(Number)
  if (!year || !month || !day) return isoDate
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)))
}

/** The courier's status, told the same way to customers and the warehouse team. */
export const TRACKING_STATUS: Record<ParcelTrackingStatus, { label: string; tone: string }> = {
  NOT_FOUND: { label: 'Tracking not active yet', tone: 'tone-neutral' },
  IN_TRANSIT: { label: 'In transit', tone: 'tone-moving' },
  OUT_FOR_DELIVERY: { label: 'Out for delivery', tone: 'tone-moving' },
  DELIVERED: { label: 'Delivered to the warehouse', tone: 'tone-done' },
  EXCEPTION: { label: 'Delivery problem', tone: 'tone-blocked' },
}

/** "20 Oct, 09:12", in the reader's own time zone. */
export function formatTrackingTime(iso: string) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

/** One place on a parcel's way, as the map shows it: consecutive scans there make one stop. */
export interface RouteStop {
  /** 1 for the first place the courier scanned it, and on in time order. */
  number: number
  latitude: number
  longitude: number
  /** The place as the courier named it. */
  label: string
  /** The scans at this stop, oldest first (event ids). */
  eventIds: string[]
  firstAt: string
  lastAt: string
  /** Where the parcel is now: the latest stop. */
  current: boolean
}

/** Scans name the same place when their courier labels match, or their points all but coincide. */
function samePlace(
  stop: Pick<RouteStop, 'label' | 'latitude' | 'longitude'>,
  event: { location: string | null; latitude: number | null; longitude: number | null },
) {
  const label = event.location?.trim()
  if (label && label === stop.label) return true
  return (
    event.latitude != null &&
    event.longitude != null &&
    Math.abs(stop.latitude - event.latitude) < 1e-4 &&
    Math.abs(stop.longitude - event.longitude) < 1e-4
  )
}

/**
 * A parcel's way as stops, in time order: consecutive scans at the same place
 * fold into one stop, and the last stop is where it is now. The points are the
 * centres of the areas the courier scanned in, not road positions. A scan
 * without a point joins the stop before it when it names the same place, and
 * is otherwise left off the map (it stays in the timeline).
 */
export function routeStops(tracking: Pick<ParcelTracking, 'events'> | null | undefined) {
  const stops: RouteStop[] = []
  const chronological = [...(tracking?.events ?? [])]
    .map((event, index) => ({ event, index }))
    // Oldest first; scans at the same moment keep the API's order, newest first, reversed.
    .sort(
      (left, right) =>
        new Date(left.event.at).getTime() - new Date(right.event.at).getTime() ||
        right.index - left.index,
    )
    .map(({ event }) => event)
  for (const event of chronological) {
    const previous = stops.at(-1)
    if (previous && samePlace(previous, event)) {
      previous.eventIds.push(event.id)
      previous.lastAt = event.at
      continue
    }
    if (event.latitude == null || event.longitude == null) continue
    if (!Number.isFinite(event.latitude) || !Number.isFinite(event.longitude)) continue
    stops.push({
      number: stops.length + 1,
      latitude: event.latitude,
      longitude: event.longitude,
      label: event.location?.trim() || 'Courier scan',
      eventIds: [event.id],
      firstAt: event.at,
      lastAt: event.at,
      current: false,
    })
  }
  if (stops.length) stops[stops.length - 1]!.current = true
  return stops
}

/** "广东省 广州市 越秀区 · 3 scans" */
export function stopTitle(stop: Pick<RouteStop, 'label' | 'eventIds'>) {
  return `${stop.label} · ${stop.eventIds.length} scan${stop.eventIds.length === 1 ? '' : 's'}`
}

/**
 * Expected parcels split by what their courier says: those it has delivered
 * should be on the warehouse floor waiting to be received, so they come first.
 */
export function groupParcels(view: WarehouseView, parcels: WarehouseParcel[]) {
  const delivered =
    view === 'expected'
      ? parcels.filter(
          (parcel) => parcel.status === 'EXPECTED' && parcel.tracking?.status === 'DELIVERED',
        )
      : []
  if (!delivered.length) return [{ key: 'all', title: null, description: null, parcels }]
  return [
    {
      key: 'delivered',
      title: 'Delivered by courier',
      description: 'The courier says these reached the warehouse: find, photograph and weigh them.',
      parcels: delivered,
    },
    {
      key: 'on-the-way',
      title: 'On the way',
      description: 'Not delivered yet, by their couriers’ latest news.',
      parcels: parcels.filter((parcel) => !delivered.includes(parcel)),
    },
  ].filter((group) => group.parcels.length)
}

/**
 * A consignment's packages on their way to our China warehouse. The domestic
 * leg ends there: nothing here is about leaving China.
 */
export const GATHERING_STATE: Record<
  GatheringState,
  { label: string; short: string; tone: string }
> = {
  AT_WAREHOUSE: { label: 'At our China warehouse', short: 'At China warehouse', tone: 'tone-done' },
  DELIVERED: {
    label: 'Delivered to our China warehouse',
    short: 'Delivered to China warehouse',
    tone: 'tone-done',
  },
  ON_THE_WAY: {
    label: 'On its way to our China warehouse',
    short: 'On its way to warehouse',
    tone: 'tone-moving',
  },
  NOT_ACTIVE: {
    label: 'Tracking not active yet',
    short: 'Tracking not active yet',
    tone: 'tone-neutral',
  },
  PROBLEM: { label: 'Delivery problem in China', short: 'Delivery problem', tone: 'tone-blocked' },
}

/** The courier's status for a package to our China warehouse, in the same words. */
export const CHINA_TRACKING_STATUS: Record<ParcelTrackingStatus, string> = {
  NOT_FOUND: 'Tracking not active yet',
  IN_TRANSIT: 'On its way to our China warehouse',
  OUT_FOR_DELIVERY: 'Out for delivery to our China warehouse',
  DELIVERED: 'Delivered to our China warehouse',
  EXCEPTION: 'Delivery problem',
}

const count = (value: number, noun: string) => `${value} ${noun}${value === 1 ? '' : 's'}`

/**
 * How gathering is going, counted in items: "3 of 5 items at our China
 * warehouse", or "All items at our China warehouse, ready to ship". When no
 * item carries a number yet, packages added by hand are counted instead.
 */
export function gatheringSummary(summary: ConsignmentGathering['summary']) {
  if (summary.items_total > 0 && summary.all_at_warehouse) {
    return 'All items at our China warehouse, ready to ship'
  }
  if (summary.items_with_number > 0) {
    return `${summary.items_at_warehouse} of ${count(summary.items_total, 'item')} at our China warehouse`
  }
  if (summary.packages_total > 0) {
    return `${summary.packages_at_warehouse} of ${count(summary.packages_total, 'package')} at our China warehouse`
  }
  return 'No domestic tracking numbers yet'
}
